/**
 *  版权所有 © 2026 东方闻道 All Rights Reserved
 *
 *  创建者: wangchao
 *  创建时间: 2026-08-28 14:52
 *  文件名: src/components/ppt-learning-plan-viewer/ppt-viewer/index.tsx
 *  功能描述: PPT 预览组件。轮询文件转换服务将 PPT 转为图片后逐页展示；
 *           转换失败时降级为 OfficeViewer 在线预览原文件。
 */

import { useAsync } from 'eastedu-kit';
import { PPTImagesViewer } from './ppt-images-viewer';
import React, { useMemo } from 'react';
import OfficeViewer from '../../office-view';
import { Spin } from 'antd';
import { getFileConvertResultFileIdWithPooling } from '../service';
import { isNil } from 'lodash';
import { getPreviewResources } from '../../../redux/action/lesson.action';
import { LearningPlanPreviewContext } from '../types';

interface PPTViewerProps {
  /** PPT文件ID */
  fileId: string;
  previewContext: LearningPlanPreviewContext;
}

/**
 * 根据 PPT fileId 进行预览。
 *
 * 预览流程：
 * 1. 轮询查询"PPT 转图片"结果，成功后通过 PPTImagesViewer 逐页展示图片；
 * 2. 同时请求教学资源预览接口完成后端埋点，并作为转换失败时的降级数据源；
 * 3. 转换失败时使用预览接口返回的文件地址交给 OfficeViewer 在线预览。
 */
function PPTViewer(props: PPTViewerProps) {
  const { fileId, previewContext } = props;

  // 轮询获取转换结果；任一页面转换失败/超时即视为整体失败，交由渲染层降级处理
  const convertImagesResult = useAsync(async () => {
    try {
      const convertResult = await getFileConvertResultFileIdWithPooling(fileId);
      if (
        !convertResult ||
        convertResult.status === 'CONVERT_FAIL' ||
        convertResult.status === 'CONVERT_TIMEOUT'
      ) {
        throw new Error('PPT转图片失败');
      }
      if (!convertResult.results?.length) {
        throw new Error('未找到PPT图片');
      }
      return convertResult.results;
    } catch (err) {
      console.error(err);
      throw new Error('PPT转图片失败');
    }
  }, [fileId]);

  // 提取各页转换后的图片地址，供 PPTImagesViewer 渲染
  const pptImages = useMemo(() => {
    return convertImagesResult.result?.map((item) => item.imgUrl).filter((a) => !isNil(a)) ?? [];
  }, [convertImagesResult.result]);

  // 独立调用预览接口完成后端埋点，并作为转换失败时的降级预览数据源
  const fileinfoResult = useAsync(() => {
    return getPreviewResources(
      fileId,
      previewContext.type,
      encodeURIComponent(previewContext.pptFileName),
      previewContext,
    );
  }, [
    fileId,
    previewContext.type,
    previewContext.pptFileName,
    previewContext.resourceId,
    previewContext.courseId,
    previewContext.lessonId,
    previewContext.originalFileId,
    previewContext.documentId,
  ]);

  return (() => {
    if (convertImagesResult.loading || fileinfoResult.loading) {
      return (
        <Spin
          tip="PPT加载中..."
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%,-50%)',
          }}
        />
      );
    }
    // 转换失败时使用同一次预览埋点请求返回的文件地址降级预览
    if (convertImagesResult.error) {
      const previewUrl = fileinfoResult.result?.cdnUrl || fileinfoResult.result?.url;
      if (fileinfoResult.error || !previewUrl) {
        return <span style={{ color: 'red' }}>PPT预览失败，请稍后再试！</span>;
      }
      if (previewUrl) {
        return <OfficeViewer url={previewUrl} extension="pptx" />;
      }
    }
    // 转换成功：逐页展示转换后的图片
    return <PPTImagesViewer slides={pptImages ?? []} />;
  })();
}

export { PPTViewer };
